import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AdminHeader from "../components/AdminHeader";
import { useNotification } from "../contexts/NotificationContext";

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const SubscribeUpsellPage = () => {
  const navigate = useNavigate();
  const { showInfo } = useNotification();
  const q = useQuery();
  const product = q.get("product") || "chat";
  const clientId = q.get("clientId") || "";

  const headline = product === "voice" ? "Never Miss a Call Again" : "Unlock Chat Support";
  const blurb =
    product === "voice"
      ? "Guests who prefer calling won't wait for chat. Capture those reservations with an AI agent that answers 24/7."
      : "Deliver fast, friendly help right on your site. Enable 24/7 chat with guided flows, escalation, and rich analytics.";

  return (
    <div className="bg-[#f7f8fa] dark:bg-gray-950 min-h-screen transition-colors duration-200">
      <AdminHeader activePage="Clients" />

      <div className="px-10 py-8 flex flex-col items-center">
        <div className="bg-white rounded-2xl shadow-xl p-10 w-full max-w-3xl text-center">
          {/* Icon/Illustration */}
          {product === "voice" ? (
            <div className="flex justify-center mb-6">
              <div className="relative">
                {/* Phone icon */}
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#32B1FF] to-blue-600 flex items-center justify-center shadow-lg">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                {/* Sound waves */}
                <div className="absolute -right-8 top-1/2 -translate-y-1/2 flex gap-1">
                  <div className="w-1 h-4 bg-[#32B1FF] rounded-full animate-pulse"></div>
                  <div className="w-1 h-6 bg-[#32B1FF] rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-1 h-4 bg-[#32B1FF] rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#32B1FF] to-blue-600 flex items-center justify-center shadow-lg">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
            </div>
          )}

          <h1 className="text-3xl font-bold text-gray-800 mb-2">{headline}</h1>
          <p className="text-gray-600 mb-6">{blurb}</p>

          <div className="grid sm:grid-cols-3 gap-4 text-left mb-8">
            <div className="rounded-xl border p-4">
              <div className="font-semibold text-gray-800 mb-1">
                {product === "voice" ? "Easy Integration" : "Quick Setup"}
              </div>
              <div className="text-sm text-gray-500">
                {product === "voice" ? "Forward your calls to us. That's the whole setup." : "Connect in minutes. No heavy lifting."}
              </div>
            </div>
            <div className="rounded-xl border p-4">
              <div className="font-semibold text-gray-800 mb-1">
                {product === "voice" ? "No Extra Staff Needed" : "Scales with You"}
              </div>
              <div className="text-sm text-gray-500">
                {product === "voice" ? "Handles 1 call or 100 during peak season." : "From pilot to production, we've got you."}
              </div>
            </div>
            <div className="rounded-xl border p-4">
              <div className="font-semibold text-gray-800 mb-1">
                {product === "voice" ? "Full Transcripts" : "Actionable Insights"}
              </div>
              <div className="text-sm text-gray-500">
                {product === "voice" ? "Read every call without listening to recordings." : "Dashboards and transcripts to learn & iterate."}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 justify-center">
            <button
              className="px-6 py-3 rounded-lg bg-[#32B1FF] text-white font-semibold hover:bg-[#259ae6] transition-colors"
              onClick={() => {
                showInfo(
                  'Request Received', 
                  'Thank you for your interest. A member of our team will contact you shortly.',
                  0 // Don't auto-dismiss - requires manual close
                );
              }}
            >
              Try Now
            </button>
            <button
              className="px-6 py-3 rounded-lg bg-white border text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={() => navigate(-1)}
            >
              Go Back
            </button>
          </div>

          {clientId && (
            <div className="mt-6 text-xs text-gray-400">Client ID: {clientId}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscribeUpsellPage;
